import { Link } from "react-router-dom";
import Facebook from "../../images/social_icons/facebook-f.svg";
import Instagram from "../../images/social_icons/instagram.svg";
import Twitter from "../../images/social_icons/twitter.svg";
import Linkedin from "../../images/social_icons/linkedin.svg";
import Youtube from "../../images/social_icons/youtube.svg";
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import GenInTouch from "../GenInTouch";
import { useState, useEffect } from "react";
import PrivacyPolicy from "../../pages/PrivacyPolicy"; // Import the PrivacyPolicy component

// Footer logo imports - White versions
import PropertyOmbudsmanWhite from "../../images/footer_logos/PropertyOmbudsman_White.png";
import PropertyRedressWhite from "../../images/footer_logos/PropertyRedress_White.png";
import PIWhite from "../../images/footer_logos/PI_White.png";
import ICOWhite from "../../images/footer_logos/ICO_White.png";
import AMLWhite from "../../images/footer_logos/AML_White.png";

// Footer logo imports - Black versions
import PropertyOmbudsmanBlack from "../../images/footer_logos/PropertyOmbudsman_Black.png";
import PropertyRedressBlack from "../../images/footer_logos/PropertyRedress_Black.png";
import PIBlack from "../../images/footer_logos/PI_Black.png";
import ICOBlack from "../../images/footer_logos/ICO_Black.png";
import AMLBlack from "../../images/footer_logos/AML_Black.png";

// Add a type extending ThemeProps to include siteName and footer logo configuration
interface ExtendedThemeProps extends ThemeProps {
  siteName: string;
  showPropertyOmbudsmanWhite?: boolean;
  showPropertyOmbudsmanBlack?: boolean;
  showPropertyRedressWhite?: boolean;
  showPropertyRedressBlack?: boolean;
  showPIWhite?: boolean;
  showPIBlack?: boolean;
  showICOWhite?: boolean;
  showICOBlack?: boolean;
  showAMLWhite?: boolean;
  showAMLBlack?: boolean;
  // Add registration number fields
  propertyOmbudsmanNumber?: string;
  propertyRedressNumber?: string;
  piNumber?: string;
  icoNumber?: string;
  amlNumber?: string;
}

const Footer = () => {
  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ExtendedThemeProps
  );
  
  // State to safely handle the domain name
  const [domainName, setDomainName] = useState("");
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  
  // Process the domain name safely once theme data is available
  useEffect(() => {
    if (getThemes) {
      try {
        console.log('Theme data:', getThemes); // Debug log
        // Try siteName first, then name as fallback
        const nameToUse = getThemes.siteName || getThemes.name || "";
        console.log('Name to use:', nameToUse); // Debug log
        setDomainName(cleanDomainName(nameToUse));
      } catch (error) {
        console.error("Error processing domain name:", error);
        setDomainName("");
      }
    }
  }, [getThemes]);

  const isWhiteOrShade = (color: string) => {
    if (!color) return false;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const cleanDomainName = (name: string | undefined | null): string => {
    // Handle undefined, null, or non-string values
    if (!name || typeof name !== 'string') {
      return '';
    }

    try {
      console.log('Original siteName:', name); // Debug log
      
      let cleanedName = name.trim();
      
      // Remove any prefix that ends with "." or "-"
      // This handles cases like "properties.mywebsite.co.uk" or "off-market-mywebsite.co.uk"
      const dotIndex = cleanedName.indexOf('.');
      const dashIndex = cleanedName.indexOf('-');
      
      if (dotIndex > 0) {
        // If there's a dot, remove everything before it
        cleanedName = cleanedName.substring(dotIndex + 1);
        console.log('Removed prefix before dot, result:', cleanedName);
      } else if (dashIndex > 0) {
        // If there's a dash but no dot, remove everything before the dash
        cleanedName = cleanedName.substring(dashIndex + 1);
        console.log('Removed prefix before dash, result:', cleanedName);
      }

      // If we end up with just "co.uk" or similar, return the original name
      if (cleanedName === 'co.uk' || cleanedName === 'com' || cleanedName === 'org' || cleanedName === 'net') {
        console.log('Detected TLD only, returning original:', name);
        return name;
      }

      console.log('Cleaned domain name:', cleanedName); // Debug log
      return cleanedName;
    } catch (error) {
      console.error("Error cleaning domain name:", error);
      return name || '';
    }
  };

  // Function to render footer logos with registration numbers
  const renderFooterLogos = () => {
    const logos = [];
    
    // Property Ombudsman - Show white version if selected
    if (getThemes?.showPropertyOmbudsmanWhite) {
      logos.push(
        <div key="property-ombudsman-white" className="flex flex-col items-center h-20 justify-start">
          <img
            src={PropertyOmbudsmanWhite}
            alt="Property Ombudsman"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.propertyOmbudsmanNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.propertyOmbudsmanNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // Property Ombudsman - Show black version if selected
    if (getThemes?.showPropertyOmbudsmanBlack) {
      logos.push(
        <div key="property-ombudsman-black" className="flex flex-col items-center h-20 justify-start">
          <img
            src={PropertyOmbudsmanBlack}
            alt="Property Ombudsman"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.propertyOmbudsmanNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.propertyOmbudsmanNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // Property Redress - Show white version if selected
    if (getThemes?.showPropertyRedressWhite) {
      logos.push(
        <div key="property-redress-white" className="flex flex-col items-center h-20 justify-start">
          <img
            src={PropertyRedressWhite}
            alt="Property Redress"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.propertyRedressNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.propertyRedressNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // Property Redress - Show black version if selected
    if (getThemes?.showPropertyRedressBlack) {
      logos.push(
        <div key="property-redress-black" className="flex flex-col items-center h-20 justify-start">
          <img
            src={PropertyRedressBlack}
            alt="Property Redress"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.propertyRedressNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.propertyRedressNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // PI - Show white version if selected
    if (getThemes?.showPIWhite) {
      logos.push(
        <div key="pi-white" className="flex flex-col items-center h-20 justify-start">
          <img
            src={PIWhite}
            alt="Professional Indemnity"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.piNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.piNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // PI - Show black version if selected
    if (getThemes?.showPIBlack) {
      logos.push(
        <div key="pi-black" className="flex flex-col items-center h-20 justify-start">
          <img
            src={PIBlack}
            alt="Professional Indemnity"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.piNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.piNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // ICO - Show white version if selected
    if (getThemes?.showICOWhite) {
      logos.push(
        <div key="ico-white" className="flex flex-col items-center h-20 justify-start">
          <img
            src={ICOWhite}
            alt="Information Commissioner's Office"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.icoNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.icoNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // ICO - Show black version if selected
    if (getThemes?.showICOBlack) {
      logos.push(
        <div key="ico-black" className="flex flex-col items-center h-20 justify-start">
          <img
            src={ICOBlack}
            alt="Information Commissioner's Office"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.icoNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.icoNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // AML - Show white version if selected
    if (getThemes?.showAMLWhite) {
      logos.push(
        <div key="aml-white" className="flex flex-col items-center h-20 justify-start">
          <img
            src={AMLWhite}
            alt="Anti-Money Laundering"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.amlNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.amlNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    // AML - Show black version if selected
    if (getThemes?.showAMLBlack) {
      logos.push(
        <div key="aml-black" className="flex flex-col items-center h-20 justify-start">
          <img
            src={AMLBlack}
            alt="Anti-Money Laundering"
            className="h-8 md:h-12 object-contain footer-logo"
          />
          <div className="h-6 flex items-start mt-1">
            {getThemes?.amlNumber && (
              <span 
                className="text-xs text-center"
                style={{ color: getThemes?.txtColor || '#fff' }}
              >
                {getThemes.amlNumber}
              </span>
            )}
          </div>
        </div>
      );
    }
    
    return logos;
  };

  // Check if any logos should be displayed
  const hasFooterLogos = () => {
    return getThemes?.showPropertyOmbudsmanWhite || 
           getThemes?.showPropertyOmbudsmanBlack ||
           getThemes?.showPropertyRedressWhite || 
           getThemes?.showPropertyRedressBlack ||
           getThemes?.showPIWhite || 
           getThemes?.showPIBlack ||
           getThemes?.showICOWhite || 
           getThemes?.showICOBlack ||
           getThemes?.showAMLWhite || 
           getThemes?.showAMLBlack;
  };
  
  return (
    <>
      {getThemes && (getThemes.website === "https://trustbricks.co.uk" || getThemes.website === "http://www.trustbricks.co.uk") && (
        <footer
          className="w-full py-10 px-4 sm:px-6 lg:px-8 mx-auto relative"
          style={{ 
            background: getThemes?.color1 || '#000',
            zIndex: 1000,
            position: 'relative'
          }}
        >
          {/* Grid */}
          <div className="max-w-7xl m-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-10">
            <div className="col-span-full hidden lg:col-span-1 lg:block text-left">
              <Link
                className="flex-none text-xl font-semibold dark:text-white"
                to="/"
                aria-label="Trustbricks"
              >
                <img
                  src={getThemes?.logo || ''}
                  className="w-48"
                  alt="Trustbricks logo"
                />
              </Link>
              <p className="mt-3 text-xs sm:text-sm text-white-f2f9fa">
                Unit 6, Church Farm, Church Road, Suffolk<br />
                United Kingdom, IP29 5AX
              </p>
            </div>

            <div className="text-left text-white-f2f9fa">
              <h4 className="text-lg font-medium text-white-f2f9fa uppercase">
                Stay Connected
              </h4>

              <div className="my-5 gap-5 flex text-sm">
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.facebook.com/trustbricks"
                >
                  <img src={Facebook} className="w-6 h-6" alt="Facebook icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.instagram.com/trust_bricks/"
                >
                  <img src={Instagram} className="w-6 h-6" alt="Instagram icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://twitter.com/trustbricks/"
                >
                  <img src={Twitter} className="w-6 h-6" alt="Twitter icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.linkedin.com/company/trustbricks/"
                >
                  <img src={Linkedin} className="w-6 h-6" alt="LinkedIn icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.youtube.com/channel/UCtbHPeoFa4tQaWSJLrOsrcQ/"
                >
                  <img src={Youtube} className="w-6 h-6" alt="YouTube icon" />
                </Link>
              </div>
              <img
                src="https://trustbricks.co.uk/wp-content/uploads/2022/08/PRS_Logo_v2.png"
                alt="PRS Logo"
                className="w-2/3 py-2"
              />
            </div>
          </div>

          {/* Footer Logos Section - Only show if logos are configured */}
          {hasFooterLogos() && (
            <div className="max-w-7xl m-auto">
              <div className="flex justify-center items-center gap-6 flex-wrap">
                {renderFooterLogos()}
              </div>
            </div>
          )}

          <div className="max-w-7xl m-auto pt-5">
            <div className="flex flex-row justify-evenly w-full">
              <div className="space-y-2 ml-4 lg:w-1/2 sm:w-full text-right">
                <p className="text-white-f2f9fa text-base font-medium">
                  Copyright © 2025 {domainName}
                </p>
                <div className="flex justify-end items-center space-x-4 text-sm">
                  <button
                    onClick={() => setIsPrivacyModalOpen(true)}
                    className="text-white-f2f9fa hover:text-gray-300 transition-colors duration-200 relative"
                    style={{ 
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      textDecoration: 'underline',
                      zIndex: 999999,
                      position: 'relative'
                    }}
                  >
                    Privacy Policy
                  </button>
                  <span className="text-white-f2f9fa">|</span>
                  <Link
                    className="text-white-f2f9fa hover:text-gray-300 transition-colors duration-200"
                    to="https://trustbricks.co.uk/legal-notice/"
                  >
                    Legal notice
                  </Link>
                  <span className="text-white-f2f9fa">|</span>
                  <button
                    onClick={() => setIsTermsModalOpen(true)}
                    className="text-white-f2f9fa hover:text-gray-300 transition-colors duration-200 relative"
                    style={{ 
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      textDecoration: 'underline',
                      zIndex: 999999,
                      position: 'relative'
                    }}
                  >
                    Terms & Conditions
                  </button>
                  <span className="text-white-f2f9fa">|</span>
                  <span className="text-white-f2f9fa">
                    {window.__TENANT__.siteVersion} (prod)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
      
      {getThemes && getThemes.website !== "https://trustbricks.co.uk" && getThemes.website !== "http://www.trustbricks.co.uk" && (
        <footer
          className="w-full py-5 px-4 sm:px-6 lg:px-8 mx-auto justify-center flex relative"
          style={{ 
            background: getThemes?.color1 || '#000',
            zIndex: 1000,
            position: 'relative'
          }}
        >
          <div className="w-full max-w-7xl mt-5">
            {/* Footer Logos Section - Only show if logos are configured */}
            {hasFooterLogos() && (
              <div className="flex justify-center items-center gap-6 flex-wrap">
                {renderFooterLogos()}
              </div>
            )}
            
            <div className="text-center space-y-2">
              <p
                className="text-base font-medium"
                style={{ color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff') }}
              >
                Copyright © 2025 {domainName}
              </p>
              <div className="flex justify-center items-center space-x-4 text-sm">
                <button
                  onClick={() => setIsPrivacyModalOpen(true)}
                  className="hover:text-gray-300 transition-colors duration-200 relative"
                  style={{ 
                    color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff'),
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    textDecoration: 'underline',
                    zIndex: 999999,
                    position: 'relative'
                  }}
                >
                  Privacy Policy
                </button>
                <span className="text-sm" style={{ color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff') }}>|</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("Terms & Conditions clicked!");
                    console.log("Current modal state:", isTermsModalOpen);
                    setIsTermsModalOpen(true);
                    console.log("Modal state should now be:", true);
                  }}
                  className="hover:text-gray-300 transition-colors duration-200 relative"
                  style={{ 
                    color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff'),
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    textDecoration: 'underline',
                    zIndex: 999999,
                    position: 'relative'
                  }}
                >
                  Terms & Conditions
                </button>
                <span className="text-sm" style={{ color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff') }}>|</span>
                <span className="text-sm" style={{ color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff') }}>
                  {window.__TENANT__.siteVersion} (prod)
                </span>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Terms & Conditions Modal */}
      {isTermsModalOpen && (
        <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
          <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-4xl flex flex-col" style={{ backgroundColor: 'white' }}>
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-bold">Terms & Conditions</h3>
              <button 
                onClick={() => setIsTermsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <iframe 
                src="https://mypropertygroup.net/terms-conditions-2025"
                className="w-full h-full border-0"
                title="Terms & Conditions"
              />
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {isPrivacyModalOpen && (
        <div className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
          <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-4xl flex flex-col" style={{ backgroundColor: 'white' }}>
            <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
              <h3 className="text-xl font-bold">Privacy Policy</h3>
              <button 
                onClick={() => setIsPrivacyModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              <PrivacyPolicy isModal={true} />
            </div>
          </div>
        </div>
      )}

      <GenInTouch />
    </>
  );
};

export default Footer;